import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import { useAuth } from "../hooks/AuthContext";

const PasswordRecoveryPage = () => {
  const supabase = useSupabaseClient();
  const { updatePassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checkedToken, setCheckedToken] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState("");
  const [isMobileClient, setIsMobileClient] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setIsMobileClient(searchParams.get("client_type") === "mobile");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (accessToken && refreshToken && type === "recovery") {
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            setError(error.message);
          }
          setCheckedToken(true);
        });
    } else {
      setError("Invalid or missing recovery token");
      setCheckedToken(true);
    }
  }, [supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    const success = await updatePassword(password);
    if (!success) {
      return;
    }

    if (isMobileClient) {
      setPasswordUpdated(true);
    } else {
      navigate("/login");
    }
  };

  const handleResendResetEmail = async () => {
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setIsResending(true);
    setError(null);

    try {
      await resetPassword(email);
      setError("Password reset email sent! Please check your inbox.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send reset email"
      );
    } finally {
      setIsResending(false);
    }
  };

  if (!checkedToken) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Typography variant="body1">Verifying token...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
          <Card.Header variant="cream" className="text-center">
            <Typography variant="h3" className="text-primary mb-4">
              Password Recovery Error
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="text-center space-y-4">
              <Typography variant="body1" className="text-red-500 mb-4">
                {error}
              </Typography>

              <Typography variant="body2" className="text-gray-600">
                Your password reset link may have expired or is invalid. Please
                request a new password reset email.
              </Typography>

              <div className="space-y-3">
                <TamaguiInput
                  id="email"
                  label="Email Address"
                  value={email}
                  onChangeText={setEmail}
                  placeholder="Enter your email"
                  fullWidth
                  inputType="email"
                />

                <SubmitButton
                  onClick={handleResendResetEmail}
                  isDisabled={!email.trim() || isResending}
                  isLoading={isResending}
                  className="w-full"
                >
                  {isResending ? "Sending..." : "Send New Reset Email"}
                </SubmitButton>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-full text-sm text-terracotta hover:text-terracotta-700 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (passwordUpdated && isMobileClient) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
          <Card.Header variant="cream" className="text-center">
            <Typography variant="h3" className="text-primary">
              Password Updated
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4 text-center">
              <Typography variant="body1" className="text-gray-700">
                Your new password is ready. Continue in the Harper app to log in
                on your device.
              </Typography>

              <a
                href="harper://login"
                className="inline-flex w-full items-center justify-center rounded-lg border border-terracotta-500 bg-terracotta-500 py-2 px-4 font-medium text-white transition-colors hover:bg-terracotta-600 focus:bg-terracotta-600"
              >
                Open Harper App
              </a>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
        <Card.Header variant="cream" className="text-center">
          <Typography variant="h3" className="text-primary">
            Set New Password
          </Typography>
        </Card.Header>
        <Card.Body>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TamaguiInput
              id="new-password"
              label="New Password"
              value={password}
              onChangeText={setPassword}
              fullWidth
              inputType="password"
            />
            <TamaguiInput
              id="confirm-password"
              label="Confirm Password"
              value={confirm}
              onChangeText={setConfirm}
              fullWidth
              inputType="password"
            />
            <SubmitButton
              onSubmit={handleSubmit}
              isDisabled={!password || !confirm}
            >
              Update Password
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PasswordRecoveryPage;
